import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { AudioMaster } from '../../components/AudioMaster';
import { StatusTag } from '../../components/StatusTag';
import { VideoStage } from '../../components/VideoStage';
import { ChangeComposer } from '../../components/client/ChangeComposer';
import { ReviewActions, type Panel } from '../../components/client/ReviewActions';
import { ReviewHistory } from '../../components/client/ReviewHistory';
import { ReviewSidebar } from '../../components/client/ReviewSidebar';
import { VideoTranscript } from '../../components/client/VideoTranscript';
import { clientStatusLabel, deliverableStage, isReviewable, metaLabel } from '../../utils/deliverables';

/**
 * One deliverable, as the client sees it.
 *
 * WHERE THIS IS STILL USED: a SHOW episode. A project's films are shown by
 * pages/client/Overview.tsx, which folded the old tab routes into one page
 * on 26 Sep 2026 — so a change made here that matters to a project client
 * has to be made there too. That is not a hypothetical: the timecoded
 * transcript below was built here first and showed up for nobody, because
 * no project link reaches this page any more.
 *
 * ORDER OF THE PAGE, changed 18 Sep 2026 at Tom's request: the thing being
 * reviewed always comes before the buttons that act on it. Audio used to sit
 * at the very bottom, below Approve, Request Changes and the version history
 * — so an audio-only deliverable asked you to approve something you had not
 * been shown yet. Now video, then audio, then the actions.
 *
 * The page's parts live in components/client/ rather than here. That is not
 * tidiness for its own sake: this file had grown past the point where it
 * could be rewritten in one piece, and a file that cannot be edited safely
 * is a file that stops getting fixed. Keep it small.
 */

export function EpisodeReview() {
  const {
    activeProject,
    activeDeliverable,
    reviewDeliverables,
    approveDeliverable,
    requestChanges,
    goClient,
    notify,
    canApprove,
    canComment,
    projectComplete
  } = usePortal();
  const [panel, setPanel] = useState<Panel>('idle');
  const [composerOpen, setComposerOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [timecode, setTimecode] = useState('');

  /**
   * A change-request draft loses its home if another reviewer approves the
   * deliverable while the composer is open — the composer's render condition
   * (canAddChanges) goes false the moment the status flips, and the form
   * simply unmounts. Rather than let that happen silently, this catches the
   * settled transition and says what happened, instead of the draft just
   * vanishing.
   */
  const settledTrackRef = useRef<{
    id: string;
    settled: boolean;
  }>({
    id: '',
    settled: false
  });
  useEffect(() => {
    if (!activeDeliverable) return;
    const nowSettled = activeDeliverable.status === 'Approved' || activeDeliverable.status === 'Delivered';
    const track = settledTrackRef.current;
    const sameItem = track.id === activeDeliverable.id;
    if (sameItem && nowSettled && !track.settled && composerOpen && (changeNote.trim() || timecode.trim())) {
      notify('This version was approved before your change request was sent — your draft was not submitted.');
      setComposerOpen(false);
      setChangeNote('');
      setTimecode('');
    }
    settledTrackRef.current = {
      id: activeDeliverable.id,
      settled: nowSettled
    };
  }, [activeDeliverable, composerOpen, changeNote, timecode, notify]);
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const index = reviewDeliverables.filter(entry => deliverableStage(entry.status) === deliverableStage(item.status)).findIndex(entry => entry.id === item.id);
  const number = index >= 0 ? String(index + 1).padStart(2, '0') : '';
  const inReview = isReviewable(item.status);
  const settled = item.status === 'Approved' || item.status === 'Delivered';
  const changesRequested = item.status === 'Changes Requested';
  const showVideo = item.type === 'Video' || item.type === 'Other' || item.type === 'Folder';
  const showAudio = item.type === 'Audio' || Boolean(item.audioUrl);

  /** Changes stay open for as long as this version is the current cut. */
  const canAddChanges = canComment && !projectComplete && !settled && (inReview || changesRequested);
  const currentChanges = item.changeRequests.filter(request => request.version === item.version);
  function confirmApproval() {
    approveDeliverable(item.id);
    setPanel('approved');
    setComposerOpen(false);
    notify('Portal notified');
  }
  function addChange(event: React.FormEvent) {
    event.preventDefault();
    const body = changeNote.trim();
    if (!body) return;
    requestChanges(item.id, body, timecode);
    setChangeNote('');
    setTimecode('');
    notify('Change added · Portal has been notified');
  }

  /**
   * A timecode clicked in the transcript goes straight into the change
   * request. This is the point of timing the transcript at all: the box
   * below used to ask a client to watch, pause, read the counter and type
   * "00:17" — four steps to say something the transcript already knew.
   */
  function applyTimecode(label: string) {
    setTimecode(label);
    setComposerOpen(true);
    notify(`${label} added — describe the change below`);
  }
  return <div data-id="element-1875" data-mp="1y43jbo99e8cr">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-1876" data-mp="1y4da3e24ojji">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-1877" data-mp="281rdabz1jbtv" />
        All Deliverables
      </button>

      <header className="mt-10 border-b border-rule pb-10" data-id="element-1878" data-mp="1fcarvy9u8v75">
        <p className="label text-mute" data-id="element-1879" data-mp="251hgwvtcgq0j">{metaLabel(item)}</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-1880" data-mp="250zn05u5mnvs">
          {number && `${number} — `}
          {item.name.toUpperCase()}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-1881" data-mp="2duukcrp1ba0v">
          <StatusTag status={item.status} audience="review" data-id="element-1882" data-mp="2itfimlkq7jmo" />
          {item.version && <p className="label m-0 text-mute" data-id="element-1883" data-mp="1m2n4hthcs1nj">Version {item.version}</p>}
          {item.versionDate !== '—' && <p className="label m-0 text-mute" data-id="element-1884" data-mp="1q48p6104pk8a">{item.versionDate}</p>}
          {item.runtime !== '—' && <p className="label m-0 text-mute" data-id="element-1885" data-mp="1v3bhqlyx9xzd">Runtime {item.runtime}</p>}
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-1886" data-mp="202en67ite67v">
        <div className="lg:col-span-8" data-id="element-1887" data-mp="200bpd1bf78sl">
          {showVideo && <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-1888" data-mp="36i8k0xk4ajoo">
                <h2 className="label m-0 text-graphite" data-id="element-1889" data-mp="3bjo6mu1y0y96">Latest Review Version</h2>
                <p className="label-sm m-0 text-mute" data-id="element-1890" data-mp="ztorlyda7mv1">
                  {item.version ? `${item.version} · ` : ''}Plays inside Portal
                </p>
              </div>
              <div className="mt-6" data-id="element-1891" data-mp="q3d8h4l1173k">
                <VideoStage driveUrl={item.driveUrl} label={`${item.name} — ${item.version || 'Review copy'}`} runtime={item.runtime} format={item.videoFormat} data-id="element-1892" data-mp="q2bx0z03milb" />
              </div>

              {/* An automatically generated transcript, for reference only —
                  never a separate reviewable item. Added 10 Sep 2026: a
                  transcript generated from the video is never something a
                  client asked for or needs to approve on its own, and it
                  goes stale the moment a new cut is published, so it carries
                  no comment or delete controls here — a change to the video
                  itself is how a client acts on anything they read in it.
                  Since 28 Sep 2026 it carries the time each passage was
                  said, and clicking one starts a change request there. */}
              {item.transcript?.trim() && <section aria-labelledby="video-transcript" className="mt-10" data-id="element-1893" data-mp="wmks8wt8un05">
                  <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-1894" data-mp="10n3iwnjehn0r">
                    <h2 id="video-transcript" className="label m-0 text-graphite" data-id="element-1895" data-mp="mnbya3ydp8ds">
                      Transcript
                    </h2>
                    <p className="label-sm m-0 text-mute" data-id="element-1896" data-mp="do8gr4ev111e">
                      {canAddChanges ? 'Auto-generated · Tap a time to request a change there' : 'Auto-generated · For reference'}
                    </p>
                  </div>
                  <VideoTranscript projectSlug={activeProject.slug} deliverableKey={item.id} fallback={item.transcript ?? ''} onUseTimecode={canAddChanges ? applyTimecode : undefined} className="mt-6 max-h-96 overflow-y-auto border border-rule bg-paperDeep px-6 py-6 sm:px-9 sm:py-8" data-id="element-1897" data-mp="2c3l5zx13ydaq" />
                </section>}
            </>}

          {showAudio && <section aria-labelledby="audio-review" className={showVideo ? 'mt-12' : ''} data-id="element-1898" data-mp="k47848i60iin">
              <h2 id="audio-review" className="label border-b border-rule pb-4 text-graphite" data-id="element-1899" data-mp="k61ttp19gens">
                Audio Review
              </h2>
              <AudioMaster label={`${item.name} — Audio ${item.version}`} driveUrl={item.audioUrl} data-id="element-1900" data-mp="t55f8znwh65g" />
            </section>}

          {!showVideo && !showAudio && <div className="border border-rule px-7 py-9" data-id="element-1901" data-mp="2uws9da339h65">
              <p className="label m-0 text-graphite" data-id="element-1902" data-mp="2pxp35mbdh4wl">Latest Review Version</p>
              <p className="mt-5 font-display text-2xl" data-id="element-1903" data-mp="20blopskbpkc1">{item.name}</p>
              {settled ? <p className="label mt-6 text-mute" data-id="element-1904" data-mp="1vciii4slx82h">{clientStatusLabel(item.status)}</p> : <button type="button" onClick={() => notify('Simulated · would open in Google Drive')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-1905" data-mp="1ranid3mex0qa">
                  Open for Review
                  <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-1906" data-mp="2f1b34phffku9" />
                </button>}
            </div>}

          <div className="mt-12" data-id="element-1907" data-mp="25b83i6fsv8my">
            <ReviewActions status={item.status} name={item.name} version={item.version} panel={panel} setPanel={setPanel} settled={settled} projectComplete={projectComplete} canApprove={canApprove} canAddChanges={canAddChanges} changesRequested={changesRequested} inReview={inReview} hasChanges={currentChanges.length > 0} onConfirmApproval={confirmApproval} onOpenComposer={() => setComposerOpen(true)} onGoFiles={() => goClient('files')} data-id="element-1908" data-mp="25a6q3vbbaa22" />
          </div>

          {composerOpen && canAddChanges && <ChangeComposer timecode={timecode} setTimecode={setTimecode} changeNote={changeNote} setChangeNote={setChangeNote} onSubmit={addChange} onDone={() => setComposerOpen(false)} data-id="element-1909" data-mp="3o79y0rmlcxj3" />}

          <ReviewHistory version={item.version} currentChanges={currentChanges} versions={item.versions} onWatch={version => notify(`Simulated · would play ${version}`)} data-id="element-1910" data-mp="6uz412wfbc34" />
        </div>

        <ReviewSidebar data-id="element-1911" data-mp="2bvxsxwt1as1w" />
      </div>
    </div>;
}